import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Typography, Select, MenuItem, TextField, FormControl, Autocomplete, CircularProgress } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  initialPharmacyValues,
  useBillingConfigOptions,
  PatientSourceWithTariffs,
  PharmacyFormValues,
  PharmacyItem as FormPharmacyItem,
  useCreatePharmacyInvoice,
  buildCreatePharmacyInvoiceParams,
  mapFormToPharmacyInvoicePayload,
  OptionforConssession,
} from '../newPharmacy/newPharmacyForm.helper';
import ItemSearchField, { PharmacyItem as ApiPharmacyItem, PharmacyItemBatch } from '../newPharmacy/ItemSearchField';
import BatchSelectionModal from '../newPharmacy/BatchSelectionModal';
import ItemsList from '../newPharmacy/ItemsList';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { usePatientContext } from '../../../../components/patinetProvider';
import { Formik } from 'formik';
import { useClinic } from '../../../../components/ClinicProvider';
import SummaryCard from '../../Invoice/newInvoice/SummaryCard';
import useCurrency from '../../../../components/CurrencyProvider';
import { CollectPaymentModal, PaymentDetails, PaymentFormValues, PaymentHistoryItem, buildPaymentApiPayload } from '../../collectPayment';
import { useCreatePayment, buildCreatePaymentParams } from '../../Invoice/newInvoice/newInvoiceForm.helper';
import { validationPharmacySchema } from './NewPatientPharmacyForm.helper';
import { useActivePackages, type PackageDetails } from '../newPharmacy/packageSelect.helper';

const NewPharmacyForm = () => {
  /**
   * -----------------------------------------------------------------------
   * Local component state 
   * -----------------------------------------------------------------------
   */
  const [patientSourceOptions, setPatientSourceOptions] = useState<PatientSourceWithTariffs[]>([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [concessionReasonOptions, setConcessionReasonOptions] = useState<OptionforConssession[]>([]);

  // Collect Payment Modal state
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const [paymentModes, setPaymentModes] = useState<{ id: number; name: string; code: string }[]>([]);
  
  // Use useRef instead of useState to avoid re-renders
  const formikRef = useRef<{
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
    setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
    values: PharmacyFormValues;
      } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientIdParam } = useParams<{ id: string }>();
  const patientId = patientIdParam ?? undefined;
  
  // Get appointment_id from route state (passed from table row click)
  const appointmentId = (location.state as { appointment_id?: number })?.appointment_id;
  
  // Get patient data from context
  const { MainpatientDetails } = usePatientContext();
  const currentPatient = MainpatientDetails;
  const patientFullName = currentPatient 
    ? `${currentPatient.first_name ?? ''} ${currentPatient.last_name ?? ''}`.trim() 
    : '';
  // eslint-disable-next-line no-console
  console.log(patientFullName);

  // Donor/Surrogate detection
  const DONOR_SURROGATE_TYPES = ['SPERM_DONOR', 'OOCYTE_DONOR', 'SURROGATE'];
  const isDonorOrSurrogate = DONOR_SURROGATE_TYPES.includes(currentPatient?.patient_type ?? '');
  const [selectedDonorPackage, setSelectedDonorPackage] = useState<PackageDetails | null>(null);

  const { ClinicId } = useClinic();
  const { getInvoiceDueDate, currency } = useCurrency();
  
  // Calculate min (today) and max (today + invoice_due_days) for due date picker
  const today = new Date();
  const minDueDate = today.toISOString().split('T')[0];
  const maxDueDate = getInvoiceDueDate?.(today)?.toISOString()?.split('T')[0] ?? minDueDate;
  
  // TODO: Get department ID from context
  const deptId = 2;

  // Active packages for patient (used for donor package auto-assign and line-item selection)
  const { data: activePackagesData, isLoading: isPackagesLoading } = useActivePackages(
    isDonorOrSurrogate ? undefined : (patientId ? Number(patientId) : undefined),
    { enabled: !!patientId, donorId: isDonorOrSurrogate ? Number(patientId) : undefined }
  );

  /**
   * -----------------------------------------------------------------------
   * API calls (TanStack Query) 
   * -----------------------------------------------------------------------
   */

  // Billing configuration options (patient source & tariff)
  const {
    data: billingConfigData,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
  } = useBillingConfigOptions(patientId);


  // Create pharmacy invoice mutation
  const createPharmacyInvoiceMutation = useCreatePharmacyInvoice();
  
  // Create payment mutation
  const createPaymentMutation = useCreatePayment();

  /**
   * Handle payment save from modal
   * Flow: Create invoice -> Get invoice ID -> Create payment
   */
  const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[]) => {
    if (!formikRef.current) {
      // eslint-disable-next-line no-console
      console.error('No form values available for payment');
      return;
    }

    if (!patientId) {
      // eslint-disable-next-line no-console
      console.error('Missing patientId');
      return;
    }

    if (!ClinicId) {
      // eslint-disable-next-line no-console
      console.error('Missing ClinicId');
      return;
    }

    const values = formikRef.current.values;

    // For new patients without prescriptions, use default values
    const treatmentDoctorId = 0; // TODO: Get from form or context
    const visitDate = new Date().toISOString().split('T')[0]; // Today's date

    // Build pharmacy invoice payload
    const invoicePayload = mapFormToPharmacyInvoicePayload(
      values,
      Number(patientId),
      Number(appointmentId ?? 0),
      treatmentDoctorId,
      visitDate
    );

    // Step 1: Create pharmacy invoice
    createPharmacyInvoiceMutation
      .mutateAsync(buildCreatePharmacyInvoiceParams(invoicePayload))
      .then((invoiceResponse) => {
        // eslint-disable-next-line no-console
        console.log('Pharmacy invoice created:', invoiceResponse);
        
        // Extract invoice ID from response
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = invoiceResponse as any;
        const invoiceId = response?.id;

        if (!invoiceId) {
          // eslint-disable-next-line no-console
          console.error('Failed to get invoice ID from response');
          return;
        }

        // Step 2: Create payment with invoice ID
        const paymentPayload = data.paymentMode === 'credit'
          ? {
            invoice_id: Number(invoiceId),
            patient_id: Number(patientId),
            clinic_id: Number(ClinicId),
            payment_source: 'PHARMACY',
            payment_against_type: 'INVOICE',
            remarks: '',
            payment_details: [], // Empty for credit mode
          }
          : {
            ...buildPaymentApiPayload(
              history,
              Number(invoiceId),
              Number(patientId),
              Number(ClinicId),
              currency,
              'INVOICE',
              ''
            ),
            payment_source: 'PHARMACY', // Add payment_source for pharmacy
          };

        // eslint-disable-next-line no-console
        console.log('Payment API Payload:', paymentPayload);

        // Call payment API
        createPaymentMutation
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .mutateAsync(buildCreatePaymentParams(paymentPayload as any))
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((paymentResponse: any) => {
            // eslint-disable-next-line no-console
            console.log('Payment created:', paymentResponse);
            // Close modal and navigate back to pharmacy list
            setCollectPaymentOpen(false);
            navigate(`/admin/billing/pharmacy/patient-pharmacy/${patientId}`);
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .catch((err: any) => {
            // eslint-disable-next-line no-console
            console.error('Failed to create payment', err);
          });
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error('Failed to create pharmacy invoice', err);
      });
  };

  /**
   * -----------------------------------------------------------------------
   * Effects – initialize dropdown options and auto-select
   * -----------------------------------------------------------------------
   */

  // Map billing config -> dropdown option lists
  useEffect(() => {
    if (!billingConfigData) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const configData = billingConfigData as any;
    const tariff_source_group = configData?.tariff_source_group;
    const CONCESSION_REASON = configData?.CONCESSION_REASON;

    if (Array.isArray(tariff_source_group) && tariff_source_group.length > 0) {
      const validOptions = tariff_source_group
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((item: any) => ({
          label: item?.label ?? item?.name ?? '',
          value: item?.value ?? item?.code ?? '',
          tariffs: item?.tariffs ?? [],
        }))
        // Filter out items without valid labels
        .filter((opt: PatientSourceWithTariffs) => opt.label.trim() !== '' && opt.value !== '');
      
      setPatientSourceOptions(validOptions);
    }

    if (Array.isArray(CONCESSION_REASON) && CONCESSION_REASON.length > 0) {
      setConcessionReasonOptions(CONCESSION_REASON);
    }

    // Extract payment modes
    if (Array.isArray(configData?.PAYMENT_METHOD) && configData.PAYMENT_METHOD.length > 0) {
      setPaymentModes(configData.PAYMENT_METHOD);
    }
  }, [billingConfigData]);


  // Auto-select first patient source
  useEffect(() => {
    if (!formikRef.current) return;
    if (!patientSourceOptions.length) return;
    
    const currentValue = formikRef.current.values?.patientSource;
    if (!currentValue && patientSourceOptions[0]) {
      const firstSource = patientSourceOptions[0];
      formikRef.current.setFieldValue('patientSource', firstSource.value, false);
      formikRef.current.setFieldTouched('patientSource', true, false);
    }
  }, [patientSourceOptions]);

  // Auto-select first tariff when patient source changes
  useEffect(() => {
    if (!formikRef.current) return;
    
    const currentSource = formikRef.current.values?.patientSource;
    if (!currentSource) return;
    
    const selectedSource = patientSourceOptions.find(
      (src) => src.value === currentSource
    );
    const tariffOptions = selectedSource?.tariffs ?? [];
    
    const currentTariff = formikRef.current.values?.tariff;
    if (!currentTariff && tariffOptions.length > 0 && tariffOptions[0]) {
      formikRef.current.setFieldValue('tariff', tariffOptions[0].tariff_code, false);
      formikRef.current.setFieldTouched('tariff', true, false);
    }
    // Note: Using patientSourceOptions as dependency instead of formikRef.current?.values?.patientSource
    // because React doesn't track ref changes. The effect will run when patientSourceOptions changes.
  }, [patientSourceOptions]);



 
  /**
   * -----------------------------------------------------------------------
   * Submit handler
   * -----------------------------------------------------------------------
   */

  const handleSubmitForm = (_values: PharmacyFormValues) => {
    // eslint-disable-next-line no-console
    console.log('handleSubmitForm called with values:', _values);
    
    // Validate we have required IDs
    if (!patientId || !appointmentId) {
      // eslint-disable-next-line no-console
      console.error('Missing patientId or appointmentId', { patientId, appointmentId });
      alert(`Missing required IDs: patientId=${patientId}, appointmentId=${appointmentId}`);
      return;
    }

    // Check if there are selected items
    const selectedItems = _values.items.filter((item) => item.checked);
    if (selectedItems.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('No items selected for billing');
      alert('Please select at least one item for billing');
      return;
    }

    // eslint-disable-next-line no-console
    console.log('Selected items:', selectedItems);

    // For new patients without prescriptions, use default values
    const treatmentDoctorId = 0; // TODO: Get from form or context
    const visitDate = new Date().toISOString().split('T')[0]; // Today's date

    // Map form values to API payload
    const payload = mapFormToPharmacyInvoicePayload(
      _values,
      Number(patientId),
      appointmentId,
      treatmentDoctorId,
      visitDate
    );

    // eslint-disable-next-line no-console
    console.log('Creating pharmacy invoice with payload:', payload);

    // Call the API using promises
    createPharmacyInvoiceMutation
      .mutateAsync(buildCreatePharmacyInvoiceParams(payload))
      .then((response) => {
        // eslint-disable-next-line no-console
        console.log('Pharmacy invoice created successfully:', response);

        // Navigate back to pharmacy list on success
        navigate(`/admin/billing/pharmacy/patient-pharmacy/${patientId}`);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to create pharmacy invoice:', error);
        alert(`Failed to create pharmacy invoice: ${error.message || 'Unknown error'}`);
      });
  };

  // Batch selection modal state
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [selectedItemForBatch, setSelectedItemForBatch] = useState<ApiPharmacyItem | null>(null);
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState<((field: string, value: unknown) => void) | null>(null);
  const [pendingCurrentItems, setPendingCurrentItems] = useState<FormPharmacyItem[]>([]);
  
  // Add ref to access ItemSearchField's clear function
  const itemSearchFieldRef = useRef<{ clearSearch: () => void } | null>(null);

  /**
   * Handle item selection from search - now opens batch modal
   */
  const handleSelectItemFromSearch = (
    apiItem: ApiPharmacyItem,
    setFieldValue: (field: string, value: unknown) => void,
    currentItems: FormPharmacyItem[]
  ) => {
    // Store the item and open batch selection modal
    setSelectedItemForBatch(apiItem);
    setPendingSetFieldValue(() => setFieldValue);
    setPendingCurrentItems(currentItems);
    setBatchModalOpen(true);
  };

  /**
   * Handle batch selection from modal
   */
  const handleBatchSelect = (batch: PharmacyItemBatch) => {
    if (!selectedItemForBatch || !pendingSetFieldValue) return;
    
    const formatDate = (dateString: string | null | undefined) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
    };

    // Calculate initial values - use batch-level tax percentages if available
    const baseMRP = batch?.base_mrp ?? 0;
    const billedQty = 1;
    const totalMRPAmount = baseMRP * billedQty;
    const sgstPercent = batch?.sgst_percent ?? selectedItemForBatch?.SGST ?? 0;
    const cgstPercent = batch?.cgst_percent ?? selectedItemForBatch?.CGST ?? 0;
    const sgstAmount = (totalMRPAmount * sgstPercent) / 100;
    const cgstAmount = (totalMRPAmount * cgstPercent) / 100;
    const netAmount = totalMRPAmount + sgstAmount + cgstAmount;

    // Map available batches from the selected item with tax percentages
    const availableBatches = (selectedItemForBatch?.batches ?? []).map((b) => ({
      batchNumber: b?.batch_number ?? '',
      expiryDate: formatDate(b?.expiry_date),
      isExpired: b?.expiry_date ? new Date(b.expiry_date) < new Date() : false,
      baseMrp: b?.base_mrp ?? 0,
      baseCostPrice: b?.base_cost ?? 0,
      sgstPercent: b?.sgst_percent ?? selectedItemForBatch?.SGST ?? 0,
      cgstPercent: b?.cgst_percent ?? selectedItemForBatch?.CGST ?? 0,
      availableStock: b?.available_stock ?? 0,
      availableUomId: b?.available_uom_id ?? 0,
      availableUom: b?.available_uom_name ?? selectedItemForBatch?.base_uom_name ?? '',
    }));

    // Map API item with selected batch to form item structure
    const newItem: FormPharmacyItem = {
      prescriptionId: 0,
      itemId: selectedItemForBatch?.item_id ?? 0,
      visitDate: new Date().toLocaleDateString('en-GB'),
      itemCode: selectedItemForBatch?.item_code ?? '',
      itemName: selectedItemForBatch?.item_name ?? '',
      batchCode: batch?.batch_number ?? '',
      expiryDate: formatDate(batch?.expiry_date),
      manufacturer: selectedItemForBatch?.item_group_name ?? '',
      availableBatches: availableBatches,
      prescribedStrength: '500 Mg',
      prescribedQty: 24,
      prescribedUOM: selectedItemForBatch?.base_uom_name ?? '',
      billedStrength: '',
      billedQty: billedQty,
      billedUOM: selectedItemForBatch?.base_uom_name ?? '',
      frequency: '1-0-1',
      rack: selectedItemForBatch?.rack_name ?? '',
      shelf: selectedItemForBatch?.shelf_name ?? '',
      container: selectedItemForBatch?.bin_name ?? '',
      checked: true,
      baseMRP: baseMRP,
      baseCostPrice: batch?.base_cost_price ?? 0,
      totalMRPAmount: totalMRPAmount,
      concessionAmount: 0,
      concessionPercent: 0,
      sgstAmount: sgstAmount,
      sgstPercent: sgstPercent,
      cgstAmount: cgstAmount,
      cgstPercent: cgstPercent,
      netAmount: netAmount,
      pregnancyClass: '',
      provider: '',
      availableStock: batch?.available_stock ?? 0,
      availableUomId: batch?.available_uom_id ?? 0,
      availableUom: batch?.available_uom_name ?? selectedItemForBatch?.base_uom_name ?? '',
    };

    pendingSetFieldValue('items', [...pendingCurrentItems, newItem]);
    
    // Reset state and clear search box
    setBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setPendingSetFieldValue(null);
    setPendingCurrentItems([]);
    
    // Clear the search box
    if (itemSearchFieldRef.current) {
      itemSearchFieldRef.current.clearSearch();
    }
  };

  /**
   * Handle closing batch modal without selection
   */
  const handleBatchModalClose = () => {
    setBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setPendingSetFieldValue(null);
    setPendingCurrentItems([]);
    
    // Clear the search box
    if (itemSearchFieldRef.current) {
      itemSearchFieldRef.current.clearSearch();
    }
  };

  /**
   * -----------------------------------------------------------------------
   * Formik wrapper
   * -----------------------------------------------------------------------
   */

  return (
    <>
      <Formik<PharmacyFormValues>
        initialValues={{
          ...initialPharmacyValues,
          dueDate: maxDueDate,
        }}
        validationSchema={validationPharmacySchema}
        enableReinitialize
        validateOnBlur={true}
        validateOnChange={false}
        onSubmit={(values, helpers) => {
          // eslint-disable-next-line no-console
          console.log('values--------------', values);
          
          setShowValidationErrors(true);
          handleSubmitForm(values);
          helpers.setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, setFieldTouched, setTouched, handleSubmit, isSubmitting, errors, touched, validateForm }) => {
          // Store Formik's setFieldValue, setFieldTouched and values in ref
          formikRef.current = { setFieldValue, setFieldTouched, values };
          return (
            <form onSubmit={handleSubmit}>
              {/* High-level loading / error banners */}
              {isConfigLoading && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Loading billing configuration...
                </Typography>
              )}
              {isConfigError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  Error loading config: {configError?.message}
                </Typography>
              )}

              {/* Main form section */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  {/* Header Row: Title + Search */}
                  <Grid2 container spacing={2} sx={{ mb: 3 }} alignItems="center">
                    {/* Title */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        New Invoice
                      </Typography>
                    </Grid2>

                    {/* Donor Package Selection */}
                    {isDonorOrSurrogate && (
                      <Grid2 size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                          size="small"
                          options={activePackagesData ?? []}
                          loading={isPackagesLoading}
                          value={selectedDonorPackage}
                          onChange={(_event, newValue) => {
                            setSelectedDonorPackage(newValue);
                            // Auto-assign the selected package to all items that have matching package_details
                            const updated = values.items.map((item) => {
                              if (!newValue) {
                                // Clear package from all items
                                return {
                                  ...item,
                                  packageId: null,
                                  packageName: undefined,
                                  is_fromPackage: false,
                                  package_pharmacy_item_id: undefined,
                                  package_pending_qty: undefined,
                                  package_total_qty: undefined,
                                  uom_description: undefined,
                                };
                              }
                              // Find matching package in item's package_details
                              const matchingPkg = item.package_details?.find(
                                (pkg) => pkg.package_id === newValue.id
                              );
                              if (matchingPkg) {
                                return {
                                  ...item,
                                  packageId: matchingPkg.package_id,
                                  packageName: matchingPkg.package_name,
                                  is_fromPackage: true,
                                  package_pharmacy_item_id: matchingPkg.package_pharmacy_item_id,
                                  package_pending_qty: matchingPkg.quantity_remaining,
                                  package_total_qty: matchingPkg.total_quantity,
                                  uom_description: matchingPkg.uom_description,
                                };
                              }
                              return item;
                            });
                            setFieldValue('items', updated);
                          }}
                          getOptionLabel={(option) => option.package_name}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Package"
                              placeholder="Select Package"
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isPackagesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                },
                              }}
                            />
                          )}
                        />
                      </Grid2>
                    )}

                    {/* Item Search */}
                    <Grid2 size={{ xs: 12, md: isDonorOrSurrogate ? 3 : 6 }}>
                      <ItemSearchField
                        ref={itemSearchFieldRef}
                        onSelectItem={(item) => handleSelectItemFromSearch(item, setFieldValue, values.items)}
                        clinicId={Number(ClinicId)}
                        deptId={deptId}
                        patientId={patientId ? Number(patientId) : undefined}
                      />
                    </Grid2>
                  </Grid2>

                </Box>

                {/* Item Details Section */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Item Details ({values.items.length})
                  </Typography>
                  
                  <ItemsList
                    items={values.items}
                    onSelectChange={(idx: number) => {
                      const updated = [...values.items];
                      updated[idx].checked = !updated[idx].checked;
                      setFieldValue('items', updated);
                    }}
                    onRemoveItem={(idx: number) => {
                      const updated = values.items.filter((_, i) => i !== idx);
                      setFieldValue('items', updated);
                    }}
                    onFieldChange={(idx: number, field: keyof FormPharmacyItem, value: string | number) => {
                      const updated = [...values.items];
                      updated[idx] = { ...updated[idx], [field]: value };
                      setFieldValue('items', updated);
                    }}
                    onItemUpdate={(idx: number, updates: Partial<FormPharmacyItem>) => {
                      const updated = [...values.items];
                      updated[idx] = { ...updated[idx], ...updates };
                      setFieldValue('items', updated);
                    }}
                    errors={Array.isArray(errors.items) ? errors.items as Array<Record<string, string>> : []}
                    touched={Array.isArray(touched.items) ? touched.items as Array<Record<string, boolean>> : []}
                  />
                </Box>

                {/* Bottom section: concession, due date, notes, summary */}
                {(() => {
                  // Compute totals from selected items
                  const totals = values.items.reduce(
                    (acc, item) => {
                      if (!item.checked) return acc;
                      const mrpAmount = Number(item.totalMRPAmount) || 0;
                      const concession = Number(item.concessionAmount) || 0;
                      const sgst = Number(item.sgstAmount) || 0;
                      const cgst = Number(item.cgstAmount) || 0;
                      const tax = sgst + cgst;
                      const net = Number(item.netAmount) || (mrpAmount - concession + tax);
                      return {
                        total: acc.total + mrpAmount,
                        concession: acc.concession + concession,
                        tax: acc.tax + tax,
                        net: acc.net + net,
                      };
                    },
                    { total: 0, concession: 0, tax: 0, net: 0 }
                  );

                  return (
                    <Grid2 container spacing={2} sx={{ mt: 3 }}>
                      <Grid2 size={{ xs: 12, md: 8 }}>
                        {/* Concession + Due Date row */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                              Concession Reason
                            </Typography>
                            <FormControl
                              fullWidth
                              size="small"
                              error={!!(showValidationErrors && errors.concessionReason && !values.concessionReason)}
                            >
                              <Select
                                value={values.concessionReason}
                                onChange={(e) => setFieldValue('concessionReason', e.target.value)}
                                displayEmpty
                              >
                                <MenuItem disabled value="">
                                  Select reason
                                </MenuItem>
                                {concessionReasonOptions.map((opt) => (
                                  <MenuItem key={opt.code} value={opt.code}>
                                    {opt.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                              Due Date <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              type="date"
                              value={values.dueDate}
                              onChange={(e) => setFieldValue('dueDate', e.target.value)}
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              error={!!(showValidationErrors && errors.dueDate && !values.dueDate)}
                              required
                              inputProps={{
                                min: minDueDate,
                                max: maxDueDate,
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Terms and conditions */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Terms and Conditions
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={values.termsAndConditions}
                            onChange={(e) => setFieldValue('termsAndConditions', e.target.value)}
                            placeholder="Ex. Payments can be made via cash, UPI, card, net banking, or bank transfer. Full payment is due at the time of service unless a credit facility is approved. Refunds, if applicable, will be processed within 7–10 working days."
                            size="small"
                          />
                        </Box>

                        {/* Legal mentions */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Legal Mentions
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={values.legalMentions}
                            onChange={(e) => setFieldValue('legalMentions', e.target.value)}
                            placeholder="Ex. Medical services may be tax-exempt under applicable laws. Patient details are confidential and protected under privacy regulations. All billing disputes must be reported within 7 days of invoice generation."
                            size="small"
                          />
                        </Box>

                        {/* Remarks & comments */}
                        <Grid2 container spacing={2}>
                          <Grid2 size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ mb: 0.5 }}>
                              <Typography variant="subtitle2">Remark</Typography>
                            </Box>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              placeholder="Type Here.."
                              value={values.remark}
                              onChange={(e) => setFieldValue('remark', e.target.value)}
                              size="small"
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ mb: 0.5 }}>
                              <Typography variant="subtitle2">Comments</Typography>
                            </Box>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              placeholder="Type Here..."
                              value={values.comments}
                              onChange={(e) => setFieldValue('comments', e.target.value)}
                              size="small"
                            />
                          </Grid2>
                        </Grid2>
                      </Grid2>

                      {/* Summary card */}
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <SummaryCard
                          total={totals.total}
                          concession={totals.concession}
                          tax={totals.tax}
                          net={totals.net}
                        />
                      </Grid2>
                    </Grid2>
                  );
                })()}
              </Box>

              {/* Footer actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    navigate(`/admin/billing/pharmacy/patient-pharmacy/${patientId}`);
                  }}
                >
                  Back to List
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || createPharmacyInvoiceMutation.isPending}
                  sx={{ fontWeight: 700 }}
                  onClick={async () => {
                    setShowValidationErrors(true);
                    const validationErrors = await validateForm();
                    const isValid = Object.keys(validationErrors).length === 0;
                    
                    if (!isValid) {
                      // Mark all fields as touched to show validation errors
                      setTouched({
                        patientSource: true,
                        tariff: true,
                        items: (values.items ?? []).map(() => ({
                          batchCode: true,
                          billedQty: true,
                          billedUOM: true,
                          prescribedQty: true,
                          prescribedUOM: true,
                        })),
                      });
                      // eslint-disable-next-line no-console
                      console.error('Form validation failed:', validationErrors);
                      return;
                    }

                    // Additional validation checks
                    const selectedItems = (values.items ?? []).filter((item) => item?.checked);
                    if (selectedItems.length === 0) {
                      // eslint-disable-next-line no-console
                      console.error('No items selected for billing');
                      alert('Please select at least one item to proceed with payment');
                      return;
                    }

                    // Check if all selected items have batch, qty, and UOM
                    const invalidItems = selectedItems.filter(
                      (item) => !item?.batchCode || !item?.billedQty || !item?.billedUOM
                    );
                    
                    if (invalidItems.length > 0) {
                      // Mark invalid items as touched
                      const touchedItems = (values.items ?? []).map((item) => {
                        const isInvalid = selectedItems.includes(item) && (!item?.batchCode || !item?.billedQty || !item?.billedUOM);
                        return isInvalid ? {
                          batchCode: true,
                          billedQty: true,
                          billedUOM: true,
                        } : {};
                      });
                      setTouched({
                        ...touched,
                        items: touchedItems,
                      });
                      // eslint-disable-next-line no-console
                      console.error('Some items are missing required fields:', invalidItems);
                      alert('Please select batch code, quantity, and UOM for all selected items');
                      return;
                    }
                    
                    // Calculate totals for payment modal
                    const totals = selectedItems.reduce(
                      (acc, item) => ({
                        total: acc.total + (item?.totalMRPAmount ?? 0),
                        concession: acc.concession + (item?.concessionAmount ?? 0),
                        tax: acc.tax + (item?.sgstAmount ?? 0) + (item?.cgstAmount ?? 0),
                        net: acc.net + (item?.netAmount ?? 0),
                      }),
                      { total: 0, concession: 0, tax: 0, net: 0 }
                    );
                    
                    // Set payment details for modal - construct patient name safely
                    const firstName = currentPatient?.first_name?.trim();
                    const lastName = currentPatient?.last_name?.trim();
                    const patientDisplayName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown Patient';
                    
                    setPaymentDetailsForModal({
                      date: new Date().toLocaleDateString('en-GB'),
                      patientName: patientDisplayName,
                      patientId: patientId ?? '',
                      patientNumericId: patientId,
                      currency: currency ?? '',
                      netAmount: totals.net,
                    });
                    
                    // Open collect payment modal
                    setCollectPaymentOpen(true);
                  }}
                >
                  {createPharmacyInvoiceMutation.isPending ? 'Processing...' : 'Collect Payment'}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>

      {/* Batch Selection Modal */}
      {selectedItemForBatch && (
        <BatchSelectionModal
          open={batchModalOpen}
          onClose={handleBatchModalClose}
          onConfirm={handleBatchSelect}
          itemName={selectedItemForBatch?.item_name ?? ''}
          itemCode={selectedItemForBatch?.item_code ?? ''}
          batches={(selectedItemForBatch?.batches ?? [])
            .filter((batch): batch is NonNullable<typeof batch> => batch != null)
            .map(batch => {
              // Helper to safely convert to number, distinguishing null/undefined from valid zeros
              const safeNumber = (val: unknown): number => {
                if (val === null || val === undefined) return 0;
                const num = Number(val);
                return isNaN(num) ? 0 : num;
              };
              
              return {
                id: batch.id ?? 0,
                batch_number: batch.batch_number ?? '',
                expiry_date: batch.expiry_date ?? '',
                base_mrp: safeNumber(batch.base_mrp),
                base_cost: safeNumber(batch.base_cost),
                base_cost_price: safeNumber(batch.base_cost_price),
                available_stock: safeNumber(batch.available_stock),
                sgst_percent: safeNumber(batch.sgst_percent),
                cgst_percent: safeNumber(batch.cgst_percent),
                available_uom_id: batch.available_uom_id ?? 0,
                available_uom_name: batch.available_uom_name ?? '',
                available_uom_static_field: batch.available_uom_static_field ?? '',
                conversion_factor: batch.conversion_factor ?? 1,
              };
            })}
          existingItems={formikRef.current?.values?.items?.map(item => ({
            itemCode: item?.itemCode ?? '',
            batchCode: item?.batchCode ?? '',
          })) ?? []}
        />
      )}

      {/* Collect Payment Modal */}
      {paymentDetailsForModal && (
        <CollectPaymentModal
          open={collectPaymentOpen}
          onClose={() => setCollectPaymentOpen(false)}
          onSave={handlePaymentSave}
          paymentDetails={paymentDetailsForModal}
          existingPaymentHistory={[]}
          walletBalance={0}
          walletAdvances={[]}
          paymentModes={paymentModes}
          showWallet={false}  // ← Disable wallet for pharmacy module
          maxCreditAmount={0}
        />
      )}
    </>
  );
};

export default NewPharmacyForm;
